interface ActiveOscillator {
  oscs: OscillatorNode[]
  gain: GainNode
}

type AudioContextConstructor = typeof AudioContext

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: AudioContextConstructor
}

/**
 * Audio engine for the Steirische Harmonika using Web Audio API.
 * Implements a musette effect with 3 detuned oscillators per note.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null
  private masterGain: GainNode | null = null
  private activeOscillators: Map<string, ActiveOscillator> = new Map()

  constructor() {
    try {
      const AudioContextClass = window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext
      if (!AudioContextClass) return

      const context = new AudioContextClass()
      const masterGain = context.createGain()
      masterGain.gain.value = 0.4
      masterGain.connect(context.destination)

      this.ctx = context
      this.masterGain = masterGain
    } catch (error) {
      console.error('AudioContext not supported', error)
    }
  }

  resume(): void {
    if (this.ctx?.state === 'suspended') {
      void this.ctx.resume()
    }
  }

  playNote(frequency: number, id: string): void {
    if (!this.ctx || !this.masterGain) return

    this.resume()
    this.stopNote(id)

    const noteGain = this.ctx.createGain()
    noteGain.connect(this.masterGain)
    noteGain.gain.setValueAtTime(0, this.ctx.currentTime)
    noteGain.gain.linearRampToValueAtTime(0.8, this.ctx.currentTime + 0.05)
    noteGain.gain.linearRampToValueAtTime(0.6, this.ctx.currentTime + 0.2)

    const oscillators = this.createMusetteOscillators(frequency, 0.0001)
    oscillators.forEach((oscillator) => {
      oscillator.connect(noteGain)
      oscillator.start()
    })

    this.activeOscillators.set(id, {oscs: oscillators, gain: noteGain})
  }

  stopNote(id: string): void {
    if (!this.ctx) return

    const active = this.activeOscillators.get(id)
    if (!active) return

    const releaseTime = 0.1

    try {
      active.gain.gain.cancelScheduledValues(this.ctx.currentTime)
      active.gain.gain.setValueAtTime(active.gain.gain.value, this.ctx.currentTime)
      active.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + releaseTime)

      const stopTime = this.ctx.currentTime + releaseTime
      active.oscs.forEach((oscillator) => oscillator.stop(stopTime))
    } catch {
      // Oscillators can already be stopped by browser audio scheduling.
    }

    this.activeOscillators.delete(id)
  }

  dispose(): void {
    this.activeOscillators.forEach((_, id) => this.stopNote(id))
    void this.ctx?.close()
    this.ctx = null
    this.masterGain = null
  }

  private createMusetteOscillators(frequency: number, detuneAmount: number): OscillatorNode[] {
    if (!this.ctx) return []

    const center = this.ctx.createOscillator()
    const sharp = this.ctx.createOscillator()
    const flat = this.ctx.createOscillator()

    center.type = 'sawtooth'
    sharp.type = 'sawtooth'
    flat.type = 'sawtooth'

    center.frequency.value = frequency
    sharp.frequency.value = frequency * (1 + detuneAmount)
    flat.frequency.value = frequency * (1 - detuneAmount)

    return [center, sharp, flat]
  }
}
